import { Alert, AlertTitle, Box, Button, Modal, Typography, IconButton } from "@mui/material"
import { Close as CloseIcon } from '@mui/icons-material'
import { schemas } from "../generated/api/client"
import { z } from "zod"
import { apiClient, apiClientHooks } from "../bootstrapping/InitApiClient"
import { queryClient } from "../bootstrapping/InitReactQuery"
import { useState } from "react"
import { ApiErrorDescription, describeApiError } from "../utils/apiError"
import ApiErrorAlert from "./ApiErrorAlert"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { CheckboxElement, TextFieldElement } from "react-hook-form-mui"
import IconButtonWithTooltip, { IconButtonWithTooltipProps } from "./IconButtonWithTooltip"

type EBulkApplication = z.infer<typeof schemas.EBulkApplication>

const RejectIconButton: React.FC<{ dbsApp: EBulkApplication } & Omit<IconButtonWithTooltipProps, 'onclick'>> = ({ dbsApp, ...iconButtonWithProps }) => {
  const [open, setOpen] = useState(false);
  // DBS-346: a refused reject/withdraw is shown in the dialog, not swallowed
  const [submitError, setSubmitError] = useState<ApiErrorDescription | undefined>(undefined)
  const handleOpen = () => { setSubmitError(undefined); setOpen(true) };
  const handleClose = () => setOpen(false);

  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 550,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
  };

  const zLocalFormScheman = z.object({
    reason: z.string(),
    notify_applicant: z.boolean().optional().default(true),
  })
  type LocalFormScheman = z.infer<typeof zLocalFormScheman>
  const form = useForm<LocalFormScheman>({
    resolver: zodResolver(zLocalFormScheman),
    defaultValues: {
      reason: '',
      notify_applicant: true,
    },
    mode: 'onBlur',
  })

  const rejectableStatuses: Array<typeof dbsApp.status> = [
    'draft', 'awaiting_customer_approval', 'awaiting_payment', 'needs_changes', 'awaiting_countersigning',
  ]
  return rejectableStatuses.includes(dbsApp.status)
    ? <>
      <IconButtonWithTooltip {...iconButtonWithProps} onClick={handleOpen} />
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography id="modal-modal-title" variant="h6" component="h2" sx={{ mb: 2 }}>
            Reject Application
          </Typography>
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>Are you sure?</AlertTitle>
            If you reject this application, this action cannot be reversed.<br/>
            <br />
            <strong>Be sure you want to do this.</strong>
          </Alert>
          <TextFieldElement
            control={form.control}
            name="reason"
            label="Reason"
            fullWidth
          />
          <Box sx={{ mt: 2 }}>
            <CheckboxElement
              control={form.control}
              name="notify_applicant"
              label="Notify Applicant"
              required={false}
            />
          </Box>
          {submitError && <ApiErrorAlert error={submitError} onClose={() => setSubmitError(undefined)} />}
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              form.handleSubmit(async (data) => {
                setSubmitError(undefined)
                try {
                  await apiClient.rejectMyEBulkApplicationByID({
                    reason: data.reason,
                    by: 'customer',
                    notify_applicant: data.notify_applicant,
                  }, { params: { id: dbsApp.id } })
                } catch (error) {
                  setSubmitError(describeApiError(error, 'Reject'))
                  return
                }
                queryClient.invalidateQueries({ queryKey: apiClientHooks.getKeyByAlias('getEBulkApplications') })
                setOpen(false)
              })()
            }}
            sx={{ mt: 2 }}
          >
            Reject Application
          </Button>
          <Button
            variant="text"
            color="primary"
            onClick={handleClose}
            sx={{ ml: 3, mt: 2 }}
          >
            Cancel
          </Button>
        </Box>
      </Modal>
    </>
    : null
}

export default RejectIconButton
